import Message from "../../../classes/Message";
import ProjectFile from "../../../classes/ProjectFile";
import { db } from "../../../boot/firebase";
import { addDoc, collection, doc, setDoc, updateDoc } from "firebase/firestore";
import { call } from "../../../util/misc";
import { extFromFile } from "../../../util/imageUpload";
import { uniqBy } from "lodash";

export const GET_MESSAGE = async ({ dispatch }, { projectID, messageID }) => {
  return dispatch("GET_DOC", {
    converter: Message,
    path: Message.getPath(projectID, messageID),
    stateKey: "messages",
    id: messageID,
  });
};

export const GET_MESSAGES = (
  { dispatch },
  { projectID, where, limit, pagination }
) => {
  const path = projectID ? Message.getPath(projectID) : null;
  return dispatch("GET_COLLECTION", {
    converter: Message,
    stateKey: "messages",
    colGroup: where && !path ? "messages" : null,
    path,
    where,
    limit,
    pagination,
  });
};

export const LISTEN_MESSAGE = async (
  { dispatch },
  { projectID, messageID, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: Message,
    path: Message.getPath(projectID, messageID),
    stateKey: "messages",
    id: messageID,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_MESSAGES = (
  { dispatch },
  { projectID, where, listenerPath, error, next, unlisten }
) => {
  const path = projectID ? Message.getPath(projectID) : null;
  return dispatch("LISTEN_COLLECTION", {
    converter: Message,
    stateKey: "messages",
    colGroup: where && !path ? "messages" : null,
    listenerPath,
    path,
    where,
    next,
    error,
    unlisten,
  });
};

// A posted file, image or not, goes up as an attachment, so the Files tab
// lists it under Attachments with everything else sent in messages, under the
// sale of the message's topic if any. Photos go up whole (UPLOAD_PROJECT_FILE
// converts HEIC); the server writes the scaled-down copy messages show
// (functions/projectFiles.js).
const uploadMessageFile = async (
  { state, dispatch },
  { projectID, message, file }
) => {
  const topic = state.topics?.[message.data.topicID];
  return dispatch("UPLOAD_PROJECT_FILE", {
    projectID,
    saleID: topic ? topic.data.saleID : null,
    type: "attachment",
    file,
  });
};

// A pasted or dropped image sits in the editor on a local blob: URL (or, from
// some sources, a base64 data: URL) until the message is sent.
const isLocalSource = (src) => /^(blob|data):/i.test(src || "");

// An inline image keeps the name of the file it came from (its `title`, set
// by MessageInput); one without (a pasted screenshot, an embedded data: URL)
// is numbered by its place among the message's images: image-1.png, ...
const inlineImageName = (img, blob, position) => {
  const extension = extFromFile(blob) || "png";
  const name = (img.getAttribute("title") || "").trim();
  if (!name) return `image-${position + 1}.${extension}`;
  return /\.[a-z0-9]+$/i.test(name) ? name : `${name}.${extension}`;
};

// Uploads each inline image still on a local source and points it at its
// project file. The text is only re-serialized when there is one.
const uploadInlineImages = async (
  context,
  { projectID, message, uploaded }
) => {
  const text = message.data.text || "";
  if (!/<img[^>]+src="(blob|data):/i.test(text)) {
    return text;
  }
  const body = new DOMParser().parseFromString(text, "text/html").body;
  const images = [...body.querySelectorAll("img")];
  for (const [position, img] of images.entries()) {
    const src = img.getAttribute("src");
    if (!isLocalSource(src)) continue;
    const blob = await (await fetch(src)).blob();
    const file = new File([blob], inlineImageName(img, blob, position), {
      type: blob.type || `image/${extFromFile(blob) || "png"}`,
    });
    const projectFile = await uploadMessageFile(context, {
      projectID,
      message,
      file,
    });
    uploaded.push(Message.fileEntry(projectFile, true));
    // Shown from the display copy; the viewer zooms into the original. Until
    // the copy exists, Message.vue falls back to the original.
    const original = projectFile.data.file.url;
    img.setAttribute("src", projectFile.displayURL || original);
    img.setAttribute("data-original-src", original);
    img.setAttribute("data-file-id", projectFile.id);
    // As uploaded (a HEIC is a .jpg by now): the viewer's title
    img.setAttribute("title", projectFile.data.file.name || file.name);
  }
  return body.innerHTML;
};

const deleteMessageFiles = (dispatch, projectID, files) =>
  Promise.all(
    files.map((file) =>
      dispatch(
        "DELETE_PROJECT_FILE",
        new ProjectFile(projectID, file.id, { storagePath: file.storagePath })
      ).catch((error) => console.error(error))
    )
  );

// `attachments`: Files to post with the message beyond its inline images.
// Every new file is up before the message is written, so nobody sees a
// message pointing at nothing; if anything fails, what did go up is removed
// again. Files the message no longer has (an inline image taken out of the
// text, an attachment removed) are deleted once it is saved.
export const SAVE_MESSAGE = async (
  { state, dispatch },
  { projectID, message, attachments = [] }
) => {
  const context = { state, dispatch };
  const previousFiles = message.id
    ? state.messages?.[message.id]?.data.files || []
    : [];
  const uploaded = [];

  try {
    message.data.text = await uploadInlineImages(context, {
      projectID,
      message,
      uploaded,
    });
    for (const file of attachments) {
      const projectFile = await uploadMessageFile(context, {
        projectID,
        message,
        file,
      });
      uploaded.push(Message.fileEntry(projectFile, false));
    }

    const inlineIDs = new Set(Message.inlineFileIDs(message.data.text));
    const offered = [...(message.data.files || []), ...uploaded];
    message.data.files = offered.filter(
      (file) => !file.inline || inlineIDs.has(file.id)
    );

    if (!message.id) {
      // New Message
      message.data.created = new Date();
      message.data.createdBy = state.user.id;
      const colRef = collection(db, Message.getPath(projectID)).withConverter(
        Message
      );
      const docRef = await addDoc(colRef, message);
      message.id = docRef.id;
    } else {
      // Existing Message
      message.data.modified = new Date();
      message.data.modifiedBy = state.user.id;
      await setDoc(doc(db, message.path).withConverter(Message), message);
    }

    const keptIDs = new Set(message.data.files.map((file) => file.id));
    const dropped = uniqBy([...previousFiles, ...offered], "id").filter(
      (file) => !keptIDs.has(file.id)
    );
    await deleteMessageFiles(dispatch, projectID, dropped);
  } catch (error) {
    await deleteMessageFiles(dispatch, projectID, uploaded);
    throw error;
  }
  return message;
};

export const UPDATE_MESSAGE = async (
  { state },
  { projectID, messageID, changes }
) => {
  changes.modified = new Date();
  changes.modifiedBy = state.user.id;
  await updateDoc(doc(db, Message.getPath(projectID, messageID)), changes);
  return updateSale();
};

// The message's files go with it (after it, so a failed delete leaves no
// message pointing at missing files).
export const DELETE_MESSAGE = async (
  { state, dispatch },
  { projectID, messageID }
) => {
  const files = state?.messages?.[messageID]?.data.files || [];
  const { deleteDoc } = await import("firebase/firestore");
  await deleteDoc(doc(db, Message.getPath(projectID, messageID)));
  await deleteMessageFiles(dispatch, projectID, files);
};

// Scrolling a thread into view marks many messages seen at once. Since
// messageSeen also marks every earlier message in the thread seen, only the
// newest one needs to be reported, so pending requests are coalesced per
// thread. `topicID` is the topic the messenger is filtered to (null: All, the
// whole project), and only that topic's messages are marked.
const SEEN_COALESCE_DELAY = 250;
const pendingSeen = new Map();

export const SEEN_MESSAGE = (
  { state },
  { projectID, messageID, topicID = null }
) => {
  const created = state.messages[messageID]?.data.created || null;
  const thread = topicID ? `${projectID}/${topicID}` : projectID;
  let pending = pendingSeen.get(thread);

  if (pending) {
    clearTimeout(pending.timer);
  } else {
    pending = {};
    pending.promise = new Promise((resolve, reject) => {
      pending.resolve = resolve;
      pending.reject = reject;
    });
    pendingSeen.set(thread, pending);
  }

  // Report only the newest message; earlier ones are implied
  if (
    !pending.messageID ||
    (created && (!pending.created || created > pending.created))
  ) {
    pending.messageID = messageID;
    pending.created = created;
  }

  pending.timer = setTimeout(() => {
    pendingSeen.delete(thread);
    call("messageSeen", {
      projectID,
      messageID: pending.messageID,
      topicID,
    }).then(pending.resolve, pending.reject);
  }, SEEN_COALESCE_DELAY);

  return pending.promise;
};

// Marks the message and everything newer in the thread unread (`topicID` as
// for SEEN_MESSAGE).
export const MARK_UNREAD = async (
  context,
  { projectID, messageID, topicID = null }
) => {
  return call("messageMarkUnread", { projectID, messageID, topicID });
};
